import { useEffect, useMemo, useState } from "react";
import api from "@/api/client";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, Pencil, Flame, IndianRupee, CalendarClock, BellOff, Bell } from "lucide-react";
import { ReminderOffForm, setLeadReminders } from "@/components/LeadReminders";
import { LeadHistory } from "@/components/LeadHistory";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { fmtDate } from "@/utils/date";

const STATUS_META = {
  new:                { label: "New",              cls: "bg-white/10 text-white/70 border border-white/15" },
  call_scheduled:     { label: "Call Scheduled",   cls: "bg-sky-500/20 text-sky-300 border border-sky-500/30" },
  awaiting_quote:     { label: "Awaiting Quote",   cls: "bg-white/10 text-white/60 border border-white/15" },
  quote_sent:         { label: "Quote Sent",       cls: "bg-blue-500/20 text-blue-300 border border-blue-500/30" },
  awaiting_approval:  { label: "Awaiting Approval",cls: "bg-cyan-500/20 text-cyan-300 border border-cyan-500/30" },
  work_started:       { label: "Work Started",     cls: "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" },
  closed_won:         { label: "Closed · Won",     cls: "bg-emerald-500/30 text-emerald-200 border border-emerald-500/40" },
  closed_lost:        { label: "Closed · Lost",    cls: "bg-red-500/20 text-red-300 border border-red-500/30" },
};
const PIPELINE_ORDER = ["new", "call_scheduled", "quote_sent", "awaiting_quote", "awaiting_approval", "work_started"];

function LeadDialog({ open, onOpenChange, existing, onSaved }) {
  const [f, setF] = useState({ client: "", project_title: "", notes: "", status: "new", quoted_amount: "", next_action_date: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      if (existing) {
        setF({
          client: existing.client || "",
          project_title: existing.project_title || "",
          notes: existing.notes || "",
          status: existing.status || "new",
          quoted_amount: existing.quoted_amount ?? "",
          next_action_date: existing.next_action_date || "",
        });
      } else {
        setF({ client: "", project_title: "", notes: "", status: "new", quoted_amount: "", next_action_date: "" });
      }
    }
  }, [open, existing]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.client.trim() || !f.project_title.trim()) {
      toast.error("Client and Project title are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        client: f.client.trim(),
        project_title: f.project_title.trim(),
        notes: f.notes || "",
        status: f.status,
        quoted_amount: f.quoted_amount === "" ? null : Number(f.quoted_amount),
        next_action_date: f.next_action_date || null,
      };
      if (existing) await api.patch(`/hot-leads/${existing.id}`, payload);
      else await api.post("/hot-leads", payload);
      toast.success(existing ? "Lead updated" : "Lead added");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-hero text-2xl uppercase">{existing ? "Edit Lead" : "New Hot Lead"}</DialogTitle>
          <DialogDescription className="text-white/50 text-xs">
            Add a client that Sales is chasing. Update its stage as it moves through the pipeline.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="overline">Client</Label>
            <Input data-testid="hot-lead-client" value={f.client} onChange={(e) => setF({ ...f, client: e.target.value })}
              className="bg-black/40 border-white/10 mt-2 focus-visible:ring-red-500" placeholder="e.g. Acme Studios" />
          </div>
          <div>
            <Label className="overline">Project</Label>
            <Input data-testid="hot-lead-project" value={f.project_title} onChange={(e) => setF({ ...f, project_title: e.target.value })}
              className="bg-black/40 border-white/10 mt-2 focus-visible:ring-red-500" placeholder="Brand film · Q3 launch" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="overline">Stage</Label>
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger data-testid="hot-lead-status" className="bg-black/40 border-white/10 mt-2"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-surface border-white/10 text-white">
                  {Object.entries(STATUS_META).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Quote (₹)</Label>
              <Input type="number" data-testid="hot-lead-amount" value={f.quoted_amount} onChange={(e) => setF({ ...f, quoted_amount: e.target.value })}
                className="bg-black/40 border-white/10 mt-2 focus-visible:ring-red-500" placeholder="80000" />
            </div>
          </div>
          <div>
            <Label className="overline">Next Action Date</Label>
            <Input type="date" data-testid="hot-lead-next-action" value={f.next_action_date} onChange={(e) => setF({ ...f, next_action_date: e.target.value })}
              className="bg-black/40 border-white/10 mt-2 focus-visible:ring-red-500 [color-scheme:dark]" />
          </div>
          <div>
            <Label className="overline">Notes</Label>
            <Textarea data-testid="hot-lead-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}
              className="bg-black/40 border-white/10 mt-2 min-h-[80px] focus-visible:ring-red-500" placeholder="Discovery call summary, budget, decision maker..." />
          </div>
          <Button data-testid="hot-lead-save" disabled={saving} className="w-full bg-[#E11D2E] hover:bg-red-700">
            {saving ? "Saving..." : existing ? "Save changes" : "Add Lead"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function LeadReminderControl({ lead, onChanged }) {
  const [form, setForm] = useState(false);
  if (["closed_won", "closed_lost"].includes(lead.status)) return null;
  if (lead.reminders_off) {
    return (
      <div className="mt-2 flex items-center gap-2 text-[11px]" data-testid={`hot-lead-reminders-off-${lead.id}`}>
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-300 border border-amber-500/30 px-2 py-0.5 truncate" title={lead.reminders_off_reason}>
          <BellOff size={11} /> Reminders off · {lead.reminders_off_reason}
        </span>
        <button type="button" data-testid={`hot-lead-reminders-on-${lead.id}`} onClick={async () => { const d = await setLeadReminders(lead.id, false); if (d) onChanged(d); }}
          className="shrink-0 inline-flex items-center gap-1 text-white/60 hover:text-white"><Bell size={11} /> Turn on</button>
      </div>
    );
  }
  if (form) return <ReminderOffForm leadId={lead.id} onCancel={() => setForm(false)} onDone={(d) => { setForm(false); onChanged(d); }} />;
  return (
    <button type="button" data-testid={`hot-lead-reminders-off-btn-${lead.id}`} onClick={() => setForm(true)}
      className="mt-2 text-[11px] text-white/45 hover:text-white inline-flex items-center gap-1"><BellOff size={11} /> Don't remind</button>
  );
}

export default function HotLeads() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("");

  const allowed = user?.is_super_admin || user?.is_admin || user?.team === "Sales Team";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hot-leads");
      setRows(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Hot Leads");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (allowed) load(); }, [allowed]);

  const remove = async (r) => {
    if (!window.confirm(`Delete lead “${r.client} · ${r.project_title}”?`)) return;
    try {
      await api.delete(`/hot-leads/${r.id}`);
      toast.success("Lead deleted");
      setRows((prev) => prev.filter((x) => x.id !== r.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const grouped = useMemo(() => {
    const g = Object.fromEntries(PIPELINE_ORDER.map((k) => [k, []]));
    g.closed_won = []; g.closed_lost = [];
    rows.forEach((r) => {
      if (filter && !`${r.client} ${r.project_title}`.toLowerCase().includes(filter.toLowerCase())) return;
      (g[r.status] || g.new).push(r);
    });
    return g;
  }, [rows, filter]);

  if (!allowed) {
    return (
      <div className="p-8 max-w-lg">
        <h1 className="font-hero text-3xl uppercase text-red-400">Restricted</h1>
        <p className="text-white/60 text-sm mt-2">Hot Leads is available to the Sales Team, Admins and Super Admins.</p>
      </div>
    );
  }

  const totalPipeline = rows
    .filter((r) => !["closed_won", "closed_lost"].includes(r.status) && r.quoted_amount)
    .reduce((sum, r) => sum + Number(r.quoted_amount || 0), 0);

  return (
    <div className="p-6 sm:p-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <div className="overline flex items-center gap-2"><Flame size={12} className="text-[#E11D2E]" /> Sales Pipeline</div>
          <h1 className="font-hero text-4xl uppercase leading-none mt-1">Hot Leads</h1>
          <p className="text-white/50 text-xs mt-2">Every conversation Sales is chasing, grouped by stage.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="overline">Open Pipeline</div>
            <div className="font-mono text-lg text-[#E11D2E]">₹{totalPipeline.toLocaleString("en-IN")}</div>
          </div>
          <Button data-testid="hot-lead-add-btn" onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-[#E11D2E] hover:bg-red-700">
            <Plus size={14} className="mr-1" /> New Lead
          </Button>
        </div>
      </div>

      <Input
        data-testid="hot-lead-search"
        placeholder="Search by client or project…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="mb-6 max-w-md bg-black/40 border-white/10 focus-visible:ring-red-500"
      />

      {loading ? (
        <div className="text-white/40 text-sm">Loading pipeline…</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[...PIPELINE_ORDER, "closed_won", "closed_lost"].map((k) => {
            const meta = STATUS_META[k];
            const items = grouped[k] || [];
            // Sum quoted amounts inside this stage — so managers can see "how
            // much value" is sitting in New / Call Scheduled / WON / Lost etc.
            const stageTotal = items.reduce((sum, r) => sum + Number(r.quoted_amount || 0), 0);
            return (
              <div key={k} className="border border-white/8 rounded-xl bg-black/25 p-4">
                <div className="flex items-center justify-between mb-3 gap-2">
                  <div className={`text-xs px-2 py-0.5 rounded font-medium ${meta.cls}`}>{meta.label}</div>
                  <div className="flex items-center gap-2 text-xs font-mono">
                    <span
                      data-testid={`hot-lead-col-total-${k}`}
                      className={`inline-flex items-center gap-0.5 ${
                        k === "closed_won" ? "text-emerald-300" : k === "closed_lost" ? "text-red-300/70" : "text-white/70"
                      }`}
                      title="Total quoted amount in this stage"
                    ><IndianRupee size={10} />{stageTotal.toLocaleString("en-IN")}</span>
                    <span className="text-white/25">·</span>
                    <span className="text-white/40">{items.length}</span>
                  </div>
                </div>
                <div className="space-y-3">
                  {items.length === 0 && <div className="text-xs text-white/30 italic">Empty</div>}
                  {items.map((r) => (
                    <div key={r.id} className="rounded-md border border-white/8 bg-surface p-3">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div>
                          <div className="client-line text-base leading-tight" data-testid={`hot-lead-client-${r.id}`}>{r.client}</div>
                          <div className="text-sm text-white/65 leading-snug mt-0.5">{r.project_title}</div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            data-testid={`hot-lead-edit-${r.id}`}
                            onClick={() => { setEditing(r); setDialogOpen(true); }}
                            className="text-white/40 hover:text-white p-1"
                            title="Edit"
                          ><Pencil size={12} /></button>
                          {(user.is_super_admin || (user.team === "Sales Team" && user.role === "manager")) && (
                            <button
                              data-testid={`hot-lead-del-${r.id}`}
                              onClick={() => remove(r)}
                              className="text-white/40 hover:text-red-400 p-1"
                              title="Delete"
                            ><Trash2 size={12} /></button>
                          )}
                        </div>
                      </div>
                      <LeadReminderControl lead={r} onChanged={(d) => setRows((prev) => prev.map((x) => (x.id === d.id ? d : x)))} />
                      <LeadHistory lead={r} />
                      <div className="flex flex-wrap gap-3 text-xs text-white/50 mt-2">
                        {r.quoted_amount != null && (
                          <span className="inline-flex items-center gap-1"><IndianRupee size={11} />{Number(r.quoted_amount).toLocaleString("en-IN")}</span>
                        )}
                        {r.next_action_date && (
                          <span className="inline-flex items-center gap-1"><CalendarClock size={11} />{fmtDate(r.next_action_date)}</span>
                        )}
                      </div>
                      <div className="text-[0.7rem] text-white/30 mt-2">
                        By {r.created_by_name} · updated {fmtDate(r.updated_at)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <LeadDialog open={dialogOpen} onOpenChange={setDialogOpen} existing={editing} onSaved={load} />
    </div>
  );
}
